<template>
  <ISTFormInput
    data-test="ist-form-time"
    v-bind="$attrs"
    :stack-label="dense ? undefined : true"
    placeholder="hh:mm"
    :align="align"
    ref="inputref"
    :disable="localdisable"
    :disabled="localdisable"
    v-model="localvalue"
    :dense="localdense"
    :error="error || localError ? true : undefined"
    :autofocus="autofocus"
    @blur="onBlur"
    @focus="onFocus"
    @update:modelValue="onchange"
    @keyup="keyup"
    style="padding-bottom: 0"
    :class="{ dense: localdense }"
    mask="##:##"
    unmasked-value
    type="text"
  />
</template>

<script setup lang="ts">
import { add, format } from "date-fns";
import { nextTick, ref, watch, onMounted } from "vue";
import { formatTimeString, isTimeValid } from "@/utils/dateUtils";
import ISTFormInput from "@/components/IstFormInput.vue";

defineOptions({ name: "IstFormInput" });

const inputref = ref<any>(null);
const localvalue = ref<string>("");
const localdisable = ref<boolean>(false);
const localdense = ref<boolean>(false);
const localError = ref<boolean>(false);
const timeRegex = /(0[0-9]|1[0-9]|2[0-3]|[0-9]):([0-5][0-9])/;

const props = withDefaults(
  defineProps<{
    validation?: any;
    modelValue?: string;
    disable?: boolean | string;
    placeholder?: string;
    focus?: boolean | string;
    min?: string;
    max?: string;
    align?: string;
    error?: boolean | string;
    dense?: boolean | string;
    grey?: boolean | string;
    autofocus?: boolean;
  }>(),
  {
    modelValue: "",
    disable: false,
    placeholder: "",
    focus: false,
    min: "",
    max: "",
    align: "left",
    error: false,
    dense: false,
    grey: false,
    autofocus: false
  }
);

const emit = defineEmits<{
  (e: "blur"): void;
  (e: "focus"): void;
  (e: "change", val: string): void;
  (e: "input", val: string): void;
  (e: "value", val: string): void;
  (e: "update:modelValue", val: string): void;
}>();

onMounted(() => {
  localvalue.value = props.modelValue;
  localdisable.value =
    (typeof props.disable === "boolean" && props.disable) || props.disable == "true";
  localdense.value = (typeof props.dense === "boolean" && props.dense) || props.dense == "true";
  if (props.autofocus) {
    setTimeout(() => {
      (inputref.value as any)?.focus?.();
    });
  }
});

watch(
  () => props.modelValue,
  (value) => {
    localvalue.value = value as string;
  }
);
watch(
  () => props.disable,
  (value) => {
    localdisable.value = value as any;
    if (value == false && props.autofocus) {
      setTimeout(() => {
        (inputref.value as any)?.focus?.();
      });
    }
  }
);
watch(
  () => props.dense,
  (value) => {
    localdense.value = value as any;
  }
);

const onBlur = () => {
  localvalue.value = formatTimeString(localvalue.value);
  if (!isTimeValid(localvalue.value)) {
    localError.value = true;
  } else {
    localError.value = false;
  }
  onchange(localvalue.value);
  emit("blur");
};

const onFocus = () => {
  emit("focus");
};

const onchange = (val: string) => {
  emit("change", localvalue.value);
  emit("input", localvalue.value);
  emit("update:modelValue", localvalue.value);
  return val;
};

const keyup = async (event: any) => {
  let AddValue = 0;
  if (event.keyCode == "38") {
    AddValue = 1;
  }
  if (event.keyCode == "40") {
    AddValue = -1;
  }

  if (event.key == "Escape" || event.key == "Enter") {
    (document as any).activeElement.blur();
    emit("blur");
  }

  if (AddValue != 0) {
    const start = event.target.selectionStart;
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.returnValue = false;
      event.cancelBubble = true;

      const [hrs, mins]: [number, number] = GetTimeValue();
      const fakeDate = new Date(2000, 1, 1, 0, 0);
      fakeDate.setHours(hrs, mins);
      let modifiedString;

      if (event.target.selectionStart > 2) {
        modifiedString = add(fakeDate, { minutes: AddValue });
      } else {
        modifiedString = add(fakeDate, { hours: AddValue });
      }
      if (localvalue.value != format(modifiedString, "HH:mm")) {
        localvalue.value = format(modifiedString, "HH:mm");
        onchange(localvalue.value);
      }
    }
    await nextTick();
    event.target.setSelectionRange(start, start);
    return false;
  }
  return false;
};

const GetTimeValue = (): [number, number] => {
  if (localvalue.value.length == 1) {
    localvalue.value = "0" + localvalue.value;
  }
  let value = localvalue.value.replace(":", "") + "0000";
  value = value.substr(0, 2) + ":" + value.substr(2, 2);
  const res = value.match(timeRegex);
  if (res) {
    return [+res[1], +res[2]];
  }
  return [0, 0];
};
</script>

<style scoped lang="scss">
.dense {
  padding: 0;

  border: 0px;
  font-size: small;
}
.dense :deep(.q-field__control) {
  padding: 0 !important;
  min-width: 43px;
}

.dense :deep(INPUT) {
  text-align: center;
}

.grey {
  border-radius: 4px;
  background-color: #eee;
}
</style>
